import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  CreditCard,
  Phone,
  Users,
  Star,
  Bus,
  Bell,
  Clock,
  Wrench,
  Lightbulb,
  Lock,
  FileText,
  LogOut,
  Trash2,
  ChevronRight,
  Building2,
  Truck,
  Tags,
  UsersRound,
  Pencil,
  MapPin,
  Loader2,
} from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Eyebrow, Loader, Card, BackHeader } from "@/components/common";
import { PushToggle } from "@/components/PushToggle";
import { ACCOUNT_LABELS } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const VEHICLE_WORK_TYPES = [
  "Bilico frigorifero",
  "Centinato / Telonato",
  "Motrice",
  "Furgone",
  "Furgone frigo",
  "Pianale",
  "Cisterna",
  "Ribaltabile",
  "Altro",
];

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-primary" />
      <div>
        <p className="text-[11px] text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-sm font-bold text-foreground">{value}</p>
      </div>
    </div>
  );
}

function SettingLink({ to, icon: Icon, title, subtitle, badge, testId }) {
  return (
    <Link to={to} data-testid={testId}>
      <Card className="flex items-center gap-3 hover:shadow-md">
        <Icon className="h-5 w-5 text-primary" />
        <div className="flex-1">
          <p className={cnBadge(badge)}>{title}</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {badge ? (
          <span data-testid="feedback-unread-badge" className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-2 text-xs font-extrabold text-primary-foreground">
            {badge}
          </span>
        ) : null}
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </Card>
    </Link>
  );
}
function cnBadge(badge) {
  return badge ? "text-sm font-bold text-primary" : "text-sm font-bold text-foreground";
}
function cnWorkType(active) {
  return active
    ? "rounded-full bg-primary px-3.5 py-2 text-xs font-extrabold text-primary-foreground"
    : "rounded-full bg-secondary px-3.5 py-2 text-xs font-bold text-secondary-foreground hover:bg-secondary/80";
}

export default function ProfilePage() {
  const { logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [rating, setRating] = useState(null);
  const [carrierName, setCarrierName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackUnread, setFeedbackUnread] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", email: "", phone: "", vat_number: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [workTypes, setWorkTypes] = useState([]);
  const [savingWorkTypes, setSavingWorkTypes] = useState(false);

  useEffect(() => {
    apiFetch("/profile")
      .then((r) => {
        setUser(r.user);
        setRating(r.rating ?? null);
        setCarrierName(r.carrier_name ?? null);
        setWorkTypes(Array.isArray(r.user?.vehicle_types) ? r.user.vehicle_types : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    apiFetch("/feedback/unread-count").then((r) => setFeedbackUnread(r.count)).catch(() => {});
  }, []);

  const toggleWorkType = async (type) => {
    const next = workTypes.includes(type) ? workTypes.filter((t) => t !== type) : [...workTypes, type];
    setWorkTypes(next);
    setSavingWorkTypes(true);
    try {
      await apiFetch("/profile", { method: "PATCH", body: JSON.stringify({ vehicle_types: next }) });
    } catch {
      setWorkTypes(workTypes);
    } finally {
      setSavingWorkTypes(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const confirmDelete = async () => {
    if (!deletePassword) {
      setDeleteError("Inserisci la password per confermare.");
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      await apiFetch("/account/delete", { method: "POST", body: JSON.stringify({ password: deletePassword }) });
      logout();
      navigate("/login", { replace: true });
    } catch (error) {
      setDeleteError(error.message || "Eliminazione non riuscita. Riprova.");
    } finally {
      setDeleting(false);
    }
  };

  const isDriver = user?.account_type === "autista";

  const openEdit = () => {
    setForm({
      company_name: user?.company_name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      vat_number: user?.vat_number || "",
      address: user?.address || "",
    });
    setEditError("");
    setEditOpen(true);
  };

  const saveProfile = async () => {
    if (!form.company_name.trim() || form.company_name.trim().length < 2) {
      setEditError(isDriver ? "Inserisci nome e cognome." : "Inserisci la ragione sociale.");
      return;
    }
    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes("@") || !email.split("@")[1]?.includes(".")) {
      setEditError("Inserisci un indirizzo email valido.");
      return;
    }
    if (!form.phone.trim() || form.phone.trim().length < 6) {
      setEditError("Inserisci un numero di telefono valido.");
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      const body = {
        company_name: form.company_name.trim(),
        email,
        phone: form.phone.trim(),
      };
      if (!isDriver) {
        body.vat_number = form.vat_number.trim();
        body.address = form.address.trim();
      }
      const updated = await apiFetch("/profile", { method: "PATCH", body: JSON.stringify(body) });
      setUser(updated);
      setEditOpen(false);
      await refresh();
    } catch (error) {
      setEditError(error.message || "Salvataggio non riuscito. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  const role = user?.account_type === "vettore" ? "VETTORE" : user?.account_type === "autista" ? "AUTISTA" : "AZIENDA";

  return (
    <div className="space-y-4">
      <BackHeader />
      <div className="flex items-center gap-4">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          {user?.account_type === "vettore" ? <Truck className="h-7 w-7" /> : <Building2 className="h-7 w-7" />}
        </span>
        <div className="min-w-0">
          <Eyebrow>{role}</Eyebrow>
          <h1 className="truncate text-xl font-extrabold text-foreground">{user?.company_name}</h1>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </div>
      </div>

      <Card className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-extrabold text-foreground">I tuoi dati</p>
          <button
            data-testid="edit-profile-button"
            onClick={openEdit}
            className="flex items-center gap-1.5 rounded-full border border-primary px-3 py-1.5 text-xs font-extrabold text-primary hover:bg-secondary"
          >
            <Pencil className="h-4 w-4" /> Modifica profilo
          </button>
        </div>
        <InfoRow icon={CreditCard} label="Partita IVA" value={user?.vat_number || "Non inserita"} />
        <InfoRow icon={Phone} label="Telefono" value={user?.phone || "Non inserito"} />
        <InfoRow icon={Users} label="Tipologia" value={ACCOUNT_LABELS[user?.account_type] || "—"} />
        {user?.address ? <InfoRow icon={MapPin} label="Indirizzo / sede" value={user.address} /> : null}
        <InfoRow icon={Star} label="Valutazione" value={rating ? `★ ${rating.average} · ${rating.count} recensioni` : "Nessuna recensione"} />
        {carrierName ? <InfoRow icon={Bus} label="Vettore di riferimento" value={carrierName} /> : null}
      </Card>

      {user?.account_type === "vettore" ? (
        <>
          <Card className="space-y-3" data-testid="vehicle-work-types-card">
            <div className="flex items-center justify-between">
              <p className="text-sm font-extrabold text-foreground">Tipi di mezzi con cui lavori</p>
              {savingWorkTypes ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : null}
            </div>
            <p className="text-xs text-muted-foreground">Indicazione generale delle tue preferenze. Non sostituisce “La mia flotta”.</p>
            <div className="flex flex-wrap gap-2">
              {VEHICLE_WORK_TYPES.map((type) => {
                const active = workTypes.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    data-testid={`work-type-${type}`}
                    onClick={() => toggleWorkType(type)}
                    className={cnWorkType(active)}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </Card>
          <SettingLink testId="profile-routes-link" to="/carrier-routes" icon={MapPin} title="Gestisci rotte preferite" subtitle="Apri le tue rotte di ricerca carichi" />
        </>
      ) : null}

      <PushToggle />

      {user?.account_type !== "autista" ? (
        <>
          <h2 className="text-lg font-extrabold text-foreground">Impostazioni</h2>
          <SettingLink testId="preferences-link" to="/preferences" icon={Bell} title="Notifiche intelligenti" subtitle="Tratte e zone preferite" />
          <SettingLink testId="depot-stats-link" to="/depot-stats" icon={Clock} title="Tempi attesa depositi" subtitle="Attesa media calcolata sulle soste reali" />
        </>
      ) : null}

      <SettingLink testId="services-link" to="/services" icon={Wrench} title="Borsa Servizi" subtitle="Servizi per mezzi pesanti" />
      <SettingLink testId="feedback-link" to="/feedback" icon={Lightbulb} title="Aiutaci a migliorare CargoFlow" subtitle="Idee, problemi e proposte" badge={feedbackUnread > 0 ? feedbackUnread : 0} />
      {user?.account_type === "azienda" ? (
        <>
          <SettingLink testId="carriers-link" to="/carriers" icon={UsersRound} title="I miei vettori" subtitle="Vettori associati alla tua azienda" />
          <SettingLink testId="offers-history-link" to="/offers-history" icon={Tags} title="Storico proposte" subtitle="Trattative e controproposte dei vettori" />
        </>
      ) : null}

      <h2 className="text-lg font-extrabold text-foreground">Informazioni e legale</h2>
      <SettingLink testId="privacy-link" to="/privacy" icon={Lock} title="Privacy Policy" subtitle="Come trattiamo i tuoi dati" />
      <SettingLink testId="terms-link" to="/terms" icon={FileText} title="Termini e Condizioni" subtitle="Regole di utilizzo del servizio" />

      <button onClick={doLogout} data-testid="profile-logout" className="flex min-h-[52px] w-full items-center justify-center gap-2 text-sm font-extrabold text-destructive">
        <LogOut className="h-5 w-5" /> Esci dall'account
      </button>

      <button
        data-testid="delete-account-button"
        onClick={() => {
          setDeletePassword("");
          setDeleteError("");
          setDeleteOpen(true);
        }}
        className="flex w-full items-center gap-3 rounded-2xl border border-destructive p-4 text-left"
      >
        <Trash2 className="h-5 w-5 text-destructive" />
        <div className="flex-1">
          <p className="text-sm font-extrabold text-destructive">Elimina account</p>
          <p className="text-xs text-muted-foreground">Operazione irreversibile</p>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </button>

      <Dialog open={editOpen} onOpenChange={(o) => !saving && setEditOpen(o)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica profilo</DialogTitle>
          </DialogHeader>
          <div className="space-y-2.5">
            <div>
              <label className="mb-1 block text-xs font-bold text-card-foreground">
                {isDriver ? "Nome e cognome" : "Ragione sociale"}
              </label>
              <input
                data-testid="edit-company-name"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="min-h-[48px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-card-foreground">Email</label>
              <input
                data-testid="edit-email"
                type="email"
                autoCapitalize="none"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="min-h-[48px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Cambiando l'email userai il nuovo indirizzo per accedere. Deve essere unico e valido.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-card-foreground">Telefono</label>
              <input
                data-testid="edit-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="min-h-[48px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
              />
            </div>
            {!isDriver ? (
              <>
                <div>
                  <label className="mb-1 block text-xs font-bold text-card-foreground">Partita IVA</label>
                  <input
                    data-testid="edit-vat"
                    value={form.vat_number}
                    onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                    className="min-h-[48px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold text-card-foreground">Indirizzo / sede</label>
                  <input
                    data-testid="edit-address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Via, città, provincia"
                    className="min-h-[48px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
                  />
                </div>
              </>
            ) : null}
            <p className="text-[11px] text-muted-foreground">Il ruolo dell'account ({ACCOUNT_LABELS[user?.account_type]}) non è modificabile.</p>
            {editError ? <p data-testid="edit-profile-error" className="text-sm font-bold text-destructive">{editError}</p> : null}
            <button
              data-testid="edit-profile-save"
              onClick={saveProfile}
              disabled={saving}
              className="mt-1 flex min-h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} SALVA MODIFICHE
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={(o) => !deleting && setDeleteOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminare l'account?</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] leading-snug text-card-foreground">
            L'operazione è irreversibile: account e dati personali verranno eliminati. I viaggi in corso o conclusi
            resteranno visibili alle controparti in forma anonimizzata. Inserisci la password per confermare.
          </p>
          <input
            data-testid="delete-account-password"
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            placeholder="Password"
            className="min-h-[48px] rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
          />
          {deleteError ? <p data-testid="delete-account-error" className="text-sm font-bold text-destructive">{deleteError}</p> : null}
          <button
            data-testid="delete-account-confirm"
            onClick={confirmDelete}
            disabled={deleting}
            className="flex min-h-[50px] items-center justify-center gap-2 rounded-xl bg-destructive text-sm font-extrabold text-white disabled:opacity-70"
          >
            {deleting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            Elimina definitivamente
          </button>
          <button data-testid="delete-account-cancel" onClick={() => setDeleteOpen(false)} className="text-sm font-bold text-muted-foreground">
            Annulla
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
